import type { AudioOutputProvider } from './AudioOutput';

function getSpeechSynthesis(): SpeechSynthesis | undefined {
  return typeof window !== 'undefined' ? window.speechSynthesis : undefined;
}

// The Web Speech API has no formal gender field on SpeechSynthesisVoice —
// this substring list (covering the common voice names shipped by Chrome/
// Edge/Safari across platforms, for the major languages this app already
// supports) is the only practical signal for "pick a female-sounding voice"
// so every language is heard in one consistent voice instead of whatever
// mixed male/female default the OS happens to assign per locale.
const FEMALE_VOICE_NAME_HINTS = [
  'female',
  'zira',
  'susan',
  'samantha',
  'victoria',
  'karen',
  'moira',
  'tessa',
  'fiona',
  'kate',
  'serena',
  'anna',
  'ivy',
  'salli',
  'joanna',
  'aria',
  'jenny',
  'heera',
  'lekha',
  'kalpana',
  'raveena',
  'paulina',
  'amelie',
  'veena',
  'google uk english female',
];

function isLikelyFemaleVoice(voice: SpeechSynthesisVoice): boolean {
  const name = voice.name.toLowerCase();
  return FEMALE_VOICE_NAME_HINTS.some((hint) => name.includes(hint));
}

function matchesLanguage(voice: SpeechSynthesisVoice, language: string): boolean {
  const targetPrimary = language.toLowerCase().split('-')[0];
  return voice.lang.toLowerCase().split('-')[0] === targetPrimary;
}

function isIndianRegion(voice: SpeechSynthesisVoice): boolean {
  return voice.lang.toLowerCase().endsWith('-in');
}

/**
 * Best-effort only: picks a female-sounding voice for the utterance's own
 * language so responses in every language share one consistent voice,
 * rather than whichever default the browser assigns per locale. This
 * deployment targets an Indian audience, so an Indian-region voice (e.g.
 * "en-IN") for that language is preferred over any other region whenever
 * the browser has one installed. Returns null (leaving the browser's own
 * default in place) whenever voices haven't loaded yet or nothing matches —
 * never blocks/delays speech.
 */
function pickFemaleVoice(
  synth: SpeechSynthesis,
  language: string | null,
): SpeechSynthesisVoice | null {
  const voices = synth.getVoices?.() ?? [];
  if (voices.length === 0) return null;

  const candidates = language ? voices.filter((voice) => matchesLanguage(voice, language)) : voices;
  const pool = candidates.length > 0 ? candidates : voices;

  const indianRegion = pool.filter(isIndianRegion);
  return (
    indianRegion.find(isLikelyFemaleVoice) ??
    pool.find(isLikelyFemaleVoice) ??
    indianRegion[0] ??
    null
  );
}

/**
 * MVP TTS provider: the browser-native Web Speech API. No network call, no
 * API key, no server round-trip — it only ever speaks the exact text it's
 * given via SpeechSynthesisUtterance, which the browser reads aloud; it
 * cannot execute or interpret that text as anything else. Runs entirely
 * inside the synchronous call stack of the user's Play click, so it
 * respects autoplay/user-gesture restrictions without any extra handling.
 *
 * Implements the same AudioOutputProvider interface a future cloud
 * provider would, so swapping one in later requires no SDK/AudioOutput
 * changes — only a different provider passed into KiaAssistantConfig.
 */
export class SpeechSynthesisAudioOutputProvider implements AudioOutputProvider {
  private currentUtterance: SpeechSynthesisUtterance | null = null;

  speak(text: string, language: string | null = null): Promise<void> {
    const synth = getSpeechSynthesis();
    if (!synth || typeof SpeechSynthesisUtterance === 'undefined') {
      return Promise.reject(new Error('Text-to-speech is not supported in this environment.'));
    }

    // Never let two utterances overlap, regardless of caller discipline.
    synth.cancel();

    return new Promise<void>((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(text);
      // Without this the browser always uses its default voice/accent
      // (typically en-US) regardless of what language `text` is actually
      // in — matching it to the response's own language is what makes a
      // non-English reply actually get spoken correctly instead of read
      // with the wrong voice (or silently mispronounced).
      if (language) utterance.lang = language;
      // Layered on top of the line above, never instead of it: `lang` alone
      // already gets correct pronunciation from the browser's own default
      // voice for that locale; this only steers which of that locale's
      // voices gets used, so every language is heard in the same
      // consistent (female) voice rather than a per-locale default that
      // might be male.
      const femaleVoice = pickFemaleVoice(synth, language);
      if (femaleVoice) utterance.voice = femaleVoice;
      this.currentUtterance = utterance;

      const finish = (): void => {
        if (this.currentUtterance === utterance) this.currentUtterance = null;
      };

      utterance.onend = () => {
        finish();
        resolve();
      };
      utterance.onerror = (event) => {
        finish();
        // A deliberate stop()/cancel() surfaces as 'canceled' or
        // 'interrupted' — that's an expected interruption (SPEAKING ->
        // READY), not a playback failure.
        if (event.error === 'canceled' || event.error === 'interrupted') {
          resolve();
          return;
        }
        reject(new Error(`Speech synthesis failed: ${event.error}`));
      };

      synth.speak(utterance);
    });
  }

  stop(): void {
    this.currentUtterance = null;
    getSpeechSynthesis()?.cancel();
  }

  dispose(): void {
    this.stop();
  }

  /**
   * iOS Safari silently ignores speechSynthesis.speak() calls that don't
   * originate from a user gesture — no error, it just never speaks. Once a
   * speak() call *has* happened inside a real gesture, later calls from
   * anywhere (including this SDK's async auto-play, long after the
   * triggering tap) are allowed for the rest of the page. This near-silent,
   * untracked utterance exists purely to be that first call — it's not
   * wired into currentUtterance/stop() since nothing needs to interrupt it.
   */
  unlock(): void {
    const synth = getSpeechSynthesis();
    if (!synth || typeof SpeechSynthesisUtterance === 'undefined') return;
    try {
      const utterance = new SpeechSynthesisUtterance(' ');
      utterance.volume = 0;
      synth.speak(utterance);
    } catch {
      // Best-effort only — a failed unlock just means iOS may block the
      // first real reply, exactly as if this method didn't exist.
    }
  }
}
